/**
 * Single Taper Collet add-ons (Sealed Slots / Chamfered / Carbide).
 * Adds hidden add-on products to the cart with the main collet line.
 */
async function loadAddonMap(host) {
  const url = host?.dataset?.colletAddonMapUrl;
  if (!url) {
    console.error('collet-addons: missing map URL');
    return null;
  }
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  } catch (e) {
    console.error('collet-addons: could not load addon map', e);
    return null;
  }
}

function collectLineItemProperties(form) {
  const properties = {};
  const fields = [
    ...(form ? form.querySelectorAll('[name^="properties["]') : []),
    ...document.querySelectorAll('[name^="properties["]'),
  ];

  for (const field of fields) {
    const match = field.name.match(/^properties\[(.+)\]$/);
    if (!match) continue;
    let key = match[1];
    if (!key || key.startsWith('_')) continue;
    if (/^message-/i.test(key)) key = 'Custom Specifications';
    const value = String(field.value || '').trim();
    if (!value) continue;
    properties[key] = value;
  }

  return properties;
}

function collectLineQuantity(form) {
  const fromForm = form?.querySelector('[name="quantity"]');
  const associated = form?.id
    ? document.querySelector(`[form="${form.id}"][name="quantity"]`)
    : null;
  return parseInt(fromForm?.value || associated?.value || '1', 10) || 1;
}

function moneyFromCents(cents, format) {
  const amount = (Number(cents) / 100).toFixed(2);
  if (!format) return `$${amount}`;
  return format
    .replace(/\{\{\s*amount\s*\}\}/, amount)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(cents) / 100)))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
}

class ColletAddons extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.mapPromise = loadAddonMap(this);
    this.chips = [...this.querySelectorAll('.collet-addons__chip')];
    this.totalNote = this.querySelector('.collet-addons__total');

    this.chips.forEach((chip) => {
      chip.addEventListener('click', (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        const pressed = chip.getAttribute('aria-pressed') === 'true';
        chip.setAttribute('aria-pressed', pressed ? 'false' : 'true');
        this.updatePriceNote();
      });
    });

    this.bindProductForms();
    this.bindVariantChanges();
    this.observePriceUpdates();
    this.updatePriceNote();
  }

  disconnectedCallback() {
    this._forms?.forEach((form) => form.removeEventListener('submit', this._submitHandler, true));
    this._variantRoots?.forEach((root) => root.removeEventListener('change', this._variantHandler));
    this._variantInputs?.forEach((input) => input.removeEventListener('change', this._variantHandler));
    this._priceObserver?.disconnect?.();
  }

  bindVariantChanges() {
    this._variantHandler = () => this.updatePriceNote();
    this._variantRoots = [...document.querySelectorAll('variant-selects, variant-radios')];
    this._variantRoots.forEach((root) => root.addEventListener('change', this._variantHandler));

    this._variantInputs = [
      ...document.querySelectorAll(
        `#product-form-${this.dataset.sectionId}-${this.dataset.productId} [name="id"], #product-form-${this.dataset.sectionId} [name="id"], #product-form-add-to-cart-sticky [name="id"]`
      ),
    ];
    this._variantInputs.forEach((input) => input.addEventListener('change', this._variantHandler));
  }

  observePriceUpdates() {
    if (this._priceObserver) return;
    this._applyingPrices = false;
    this._priceObserver = new MutationObserver(() => {
      if (this._applyingPrices) return;
      this.updatePriceNote();
    });

    [
      `price-${this.dataset.sectionId}`,
      `block-title-price-${this.dataset.sectionId}`,
    ].forEach((id) => {
      const node = document.getElementById(id);
      if (node) this._priceObserver.observe(node, { childList: true, subtree: true });
    });
  }

  bindProductForms() {
    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;
    const forms = [
      ...document.querySelectorAll(
        `#product-form-${sectionId}-${productId}, #product-form-${sectionId}, #product-form-add-to-cart-sticky`
      ),
    ];

    this._forms = [...new Set(forms)];
    if (!this._forms.length) {
      const fallback = document.querySelector('product-form form[data-type="add-to-cart-form"]');
      if (fallback) this._forms = [fallback];
    }

    this._submitHandler = this.onSubmit.bind(this);
    this._forms.forEach((form) => form.addEventListener('submit', this._submitHandler, true));
  }

  getSelectedAddons() {
    return this.chips
      .filter((chip) => chip.getAttribute('aria-pressed') === 'true')
      .map((chip) => ({
        key: chip.dataset.addonKey,
        label: chip.dataset.addonLabel,
        amount: String(parseInt(chip.dataset.addonAmount, 10)),
      }));
  }

  getAddonCentsTotal() {
    return this.getSelectedAddons().reduce((sum, a) => sum + parseInt(a.amount, 10) * 100, 0);
  }

  getSelectedVariantId() {
    return (
      document.querySelector(
        `#product-form-${this.dataset.sectionId}-${this.dataset.productId} [name="id"], #product-form-${this.dataset.sectionId} [name="id"]`
      )?.value || ''
    );
  }

  getVariantData() {
    if (this._variantData) return this._variantData;
    const script = document.querySelector(
      `variant-radios[data-product-id="${this.dataset.productId}"] script[type="application/json"], variant-selects[data-product-id="${this.dataset.productId}"] script[type="application/json"]`
    );
    if (!script) return [];
    try {
      this._variantData = JSON.parse(script.textContent);
    } catch (e) {
      this._variantData = [];
    }
    return this._variantData;
  }

  getSelectedDiameterLabel() {
    const variantId = this.getSelectedVariantId();
    const match = this.getVariantData().find((variant) => String(variant.id) === String(variantId));
    return match?.option1 || match?.title || '';
  }

  getMainPriceCents() {
    const variantId = this.getSelectedVariantId();
    const match = this.getVariantData().find((variant) => String(variant.id) === String(variantId));
    if (match && Number.isFinite(Number(match.price))) return Number(match.price);

    const priceRoot = document.getElementById(`price-${this.dataset.sectionId}`);
    const priceEl = priceRoot?.querySelector('[data-amount]');
    const amount = priceEl?.dataset?.amount;
    if (amount) {
      // Theme strips the decimal from money_without_currency, so "205.05" becomes "20505" cents.
      const raw = String(amount).trim();
      if (/[.,]/.test(raw)) {
        const dollars = parseFloat(raw.replace(/,/g, ''));
        if (!Number.isNaN(dollars)) return Math.round(dollars * 100);
      }
      const cents = parseInt(raw.replace(/[^0-9]/g, ''), 10);
      if (!Number.isNaN(cents)) return cents;
    }
    return 0;
  }

  updatePriceNote() {
    const selected = this.getSelectedAddons();
    const addonCents = this.getAddonCentsTotal();
    const format = this.dataset.moneyFormat || '${{amount}}';
    const totalText = moneyFromCents(this.getMainPriceCents() + addonCents, format);

    if (this.totalNote) {
      if (!addonCents) {
        this.totalNote.hidden = true;
        this.totalNote.textContent = '';
      } else {
        this.totalNote.hidden = false;
        this.totalNote.textContent = `With selected options: ${totalText}`;
      }
    }

    this.updateHeaderSummary(selected, addonCents ? totalText : null);
  }

  updateHeaderSummary(selected, totalText) {
    document.querySelectorAll('.collet-addons__header-diameter').forEach((el) => el.remove());

    const diameter = this.getSelectedDiameterLabel();
    const summaryParts = [diameter, ...selected.map((addon) => addon.label)].filter(Boolean);

    document.querySelectorAll('.product__info-block--title .product__title--wrapper').forEach((wrap) => {
      let note = wrap.querySelector('.collet-addons__header-options');
      if (!summaryParts.length) {
        note?.remove();
        return;
      }
      if (!note) {
        note = document.createElement('p');
        note.className = 'collet-addons__header-options';
        wrap.querySelector('.product__title')?.after(note);
      }
      note.textContent = summaryParts.join(' · ');
    });

    const priceEls = document.querySelectorAll(
      [
        `#block-title-price-${this.dataset.sectionId} .price--final`,
        `#price-${this.dataset.sectionId} .price--final`,
        '#add-to-cart-sticky .price--final',
      ].join(', ')
    );

    this._applyingPrices = true;
    priceEls.forEach((el) => {
      if (!el.dataset.colletBasePrice) {
        el.dataset.colletBasePrice = el.innerHTML;
      }
      if (!totalText) {
        el.innerHTML = el.dataset.colletBasePrice;
        delete el.dataset.colletBasePrice;
      } else {
        el.textContent = totalText;
      }
    });
    requestAnimationFrame(() => {
      this._applyingPrices = false;
    });
  }

  async onSubmit(evt) {
    const selected = this.getSelectedAddons();
    if (!selected.length) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    const form = evt.currentTarget;
    const submitButton =
      form.querySelector('[type="submit"]') || document.querySelector(`[form="${form.id}"]`);
    if (submitButton?.classList?.contains('loading')) return;

    const cart = document.querySelector('cart-sidebar');
    const map = await this.mapPromise;
    if (!map) {
      alert('Add-on options could not be loaded. Please refresh and try again.');
      return;
    }

    const mainVariantId = form.querySelector('[name="id"]')?.value;
    const quantity = collectLineQuantity(form);
    if (!mainVariantId) return;

    const groupId = `collet-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

    const items = [
      {
        id: Number(mainVariantId),
        quantity,
        properties: {
          ...collectLineItemProperties(form),
          _collet_parent: this.dataset.productHandle || '',
          _collet_group: groupId,
        },
      },
    ];

    for (const addon of selected) {
      const entry = map[addon.key];
      const variantId = entry?.variantsByPrice?.[addon.amount];
      if (!variantId) {
        console.error('collet-addons: missing variant', addon);
        alert(`Could not add ${addon.label} (+$${addon.amount}). Please contact us.`);
        return;
      }
      items.push({
        id: Number(variantId),
        quantity,
        properties: {
          // Hidden on checkout (_ prefix). Cart UI reads this when parent line is missing.
          _for_product: this.dataset.productTitle || '',
          _collet_parent: this.dataset.productHandle || '',
          _collet_addon: addon.key,
          _collet_group: groupId,
        },
      });
    }

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
    }

    try {
      const sections = cart?.getSectionsToRender?.()?.map((s) => s.id) || [];
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const json = await res.json();
      if (!res.ok || json.status) {
        throw new Error(json.description || json.message || 'Could not add to cart');
      }

      if (cart && json.sections) {
        cart.renderContents(json);
      } else if (cart?.reload) {
        await cart.reload();
        cart.revealStickyHeaderAndOpen?.() || cart.open?.();
      } else {
        window.location.href = `${window.Shopify.routes.root}cart`;
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Could not add to cart');
    } finally {
      submitButton?.classList?.remove('loading');
      submitButton?.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('collet-addons')) {
  customElements.define('collet-addons', ColletAddons);
}
